import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Logo } from "@/components/landing/Logo";

const PAGES = {
    privacy: {
        title: "Privacy Policy",
        testId: "legal-privacy-page",
        sections: [
            {
                h: "Titolare del trattamento",
                p: "Il titolare del trattamento dei dati è Proaura s.r.l.s., contattabile all'indirizzo email proaurasrl@gmail.com.",
            },
            {
                h: "Dati raccolti",
                p: "Raccogliamo i dati necessari a completare l'acquisto e consegnare il prodotto digitale: indirizzo email e dati di pagamento (trattati direttamente da Stripe, che agisce come autonomo titolare per la parte di competenza). Non vendiamo né cediamo i tuoi dati a terzi.",
            },
            {
                h: "Finalità e base giuridica",
                p: "I dati sono trattati per: esecuzione del contratto di acquisto (consegna del PDF e email di conferma), adempimenti fiscali e contabili, e risposta a eventuali richieste di assistenza.",
            },
            {
                h: "I tuoi diritti",
                p: "Ai sensi del GDPR (Reg. UE 2016/679) puoi richiedere in qualsiasi momento accesso, rettifica, cancellazione, limitazione o portabilità dei tuoi dati scrivendo a proaurasrl@gmail.com.",
            },
        ],
    },
    cookie: {
        title: "Cookie Policy",
        testId: "legal-cookie-page",
        sections: [
            {
                h: "Cosa sono i cookie",
                p: "I cookie sono piccoli file di testo che il sito salva sul tuo dispositivo per funzionare correttamente.",
            },
            {
                h: "Cookie utilizzati da questo sito",
                p: "Questo sito utilizza cookie tecnici necessari al funzionamento (ad esempio per la sessione di pagamento) e strumenti di analisi anonimizzata per migliorare l'esperienza d'uso. Non utilizziamo cookie di profilazione pubblicitaria.",
            },
            {
                h: "Come gestire i cookie",
                p: "Puoi eliminare o bloccare i cookie in qualsiasi momento dalle impostazioni del tuo browser. La disattivazione dei cookie tecnici può compromettere il completamento dell'acquisto.",
            },
        ],
    },
    termini: {
        title: "Termini e condizioni",
        testId: "legal-termini-page",
        sections: [
            {
                h: "Oggetto",
                p: "I presenti termini regolano l'acquisto del prodotto digitale «Difesa AMM 2026 - 3 Simulazioni Complete» (PDF), venduto al prezzo di 14,90 € con pagamento unico.",
            },
            {
                h: "Consegna",
                p: "Dopo il pagamento, il PDF è disponibile per il download immediato dalla pagina di conferma ordine e viene inviato un'email con il link per scaricarlo.",
            },
            {
                h: "Natura del prodotto",
                p: "Il materiale è didattico e indipendente: non è prodotto, approvato o affiliato al Ministero della Difesa né ad alcuna pubblica amministrazione. I quesiti sono originali e realizzati esclusivamente per esercitazione; non è garantita la presenza di quesiti identici o analoghi nella prova ufficiale.",
            },
            {
                h: "Diritto di recesso",
                p: "Trattandosi di contenuto digitale fornito immediatamente, ai sensi dell'art. 59, lett. o), del Codice del Consumo il diritto di recesso non si applica una volta avvenuto il download, salvo diversa previsione di legge.",
            },
            {
                h: "Proprietà intellettuale",
                p: "Il PDF è concesso in licenza d'uso personale e non cedibile. È vietata la riproduzione, distribuzione o rivendita, anche parziale.",
            },
        ],
    },
    contatti: {
        title: "Contatti",
        testId: "legal-contatti-page",
        sections: [
            {
                h: "Assistenza",
                p: "Per qualsiasi domanda sul prodotto, sul pagamento o sul download scrivici a proaurasrl@gmail.com. Rispondiamo in genere entro 24-48 ore lavorative.",
            },
            {
                h: "Problemi con il download",
                p: "Se hai completato il pagamento ma non riesci a scaricare il PDF, controlla la cartella spam dell'email di conferma e riprova dal link ricevuto. Se il problema persiste, scrivici indicando l'email usata per l'acquisto.",
            },
        ],
    },
};

export default function LegalPage({ slug }) {
    const page = PAGES[slug] || PAGES.privacy;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    return (
        <div className="min-h-screen bg-slate-50">
            <header className="border-b border-slate-200 bg-white">
                <div className="mx-auto flex max-w-3xl items-center justify-between px-4 py-4 sm:px-6">
                    <Link to="/" aria-label="Home">
                        <Logo compact />
                    </Link>
                    <Link
                        to="/"
                        data-testid="legal-back-home-link"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 transition-colors hover:text-gold"
                    >
                        <ArrowLeft className="h-3.5 w-3.5" /> Torna alla home
                    </Link>
                </div>
            </header>
            <main className="mx-auto max-w-3xl px-4 py-14 sm:px-6" data-testid={page.testId}>
                <h1 className="font-display text-3xl font-extrabold tracking-tight text-navy-950 sm:text-4xl">
                    {page.title}
                </h1>
                <div className="mt-10 space-y-8">
                    {page.sections.map((s) => (
                        <section key={s.h}>
                            <h2 className="font-display text-lg font-bold text-navy-950">{s.h}</h2>
                            <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{s.p}</p>
                        </section>
                    ))}
                </div>
                {slug === "contatti" && (
                    <a
                        href="mailto:proaurasrl@gmail.com"
                        data-testid="contatti-email-link"
                        className="mt-12 inline-flex items-center gap-2 rounded-lg bg-gold px-6 py-3.5 text-sm font-bold text-navy-950 transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-gold-hover"
                    >
                        Scrivici: proaurasrl@gmail.com
                    </a>
                )}
            </main>
        </div>
    );
}
